import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, catchError, map, mergeMap, of, throwError } from 'rxjs';
import { Person } from '../entities/person';
import { environment } from 'src/environments/environment.development';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserService } from './user.service';
import { User } from '../entities/user';
import { MediaService } from './media.service';
import { City } from '../entities/city';
import { Locality } from '../entities/locality';
import { ServicePlace } from '../entities/service-place';
import { CategoryService } from '../entities/service';
import { isPlatformBrowser } from '@angular/common';
import { InteractionService } from './interaction.service';
import { Interaction } from '../entities/interaction';
import { SubscriptionService } from './subscription.service';

interface ApiResponse {
  status: string;
  data: Person | Person[];
}

interface ApiEditResponse {
  status: string;
  data: {
    urlId: string;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PersonService {
  
  constructor(private http: HttpClient, private userService: UserService, private mediaService: MediaService, @Inject(PLATFORM_ID) private platformId: Object, 
    private interactionService: InteractionService, private subscriptionService: SubscriptionService) { }

  private apiServicesUrl = environment.cfgApiBaseUrl;
  private personServiceUrl = this.apiServicesUrl + '/person';

  getPerson(urlId: string, count: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.personServiceUrl + '/' + urlId, { params: { 'count': count } } )
    .pipe(
      catchError(this.handleError<ApiResponse>('getPerson'))
    );
  }

  getFullProfile(urlId: string, count: number): Observable<ApiResponse> {
    return this.getPerson(urlId, count)
    .pipe(
      mergeMap(person$ => this.mediaService.getMediaByPerson(urlId)
      .pipe(
        map(media$ => {
          (person$.data as Person).mediaContent = media$.data
          return person$;
        })
      )
    ));
  }

  getSubscriptionProfile(urlId: string): Observable<ApiResponse> {
    return this.getPerson(urlId, 0)
    .pipe(
      mergeMap(person$ => this.subscriptionService.getSubscriptionsByPerson(urlId)
      .pipe(
        map(subscription$ => {
          for (let subscription of subscription$.data) {
            subscription.beginDate = new Date(subscription.beginDate!);
            subscription.issueDate = new Date(subscription.issueDate);
            subscription.endDate = new Date(subscription.endDate!);
          }
          (person$.data as Person).subscriptionDetails = subscription$.data
          return person$;
        })
      )
    ));
  }

  getPersonList(filterParam: string | null | undefined, localityParam: string | null | undefined, filterType: string): Observable<ApiResponse> {
    let filter = filterParam ? '/' + filterParam : '';
    let locality = filterParam && localityParam ? '/' + localityParam : '';
    return this.http.get<ApiResponse>(this.personServiceUrl + '-list' + filter + locality + '?filterType=' + filterType)
    .pipe(
      catchError(this.handleError<ApiResponse>('getPerson'))
    );
  }

  getPersonListAdmin(showAll: boolean): Observable<ApiResponse> {
    let token;
    if (isPlatformBrowser(this.platformId))
      token = sessionStorage.getItem('auth');
    return this.http.get<ApiResponse>(this.personServiceUrl + '-list-admin?showAll=' + showAll, {headers: new HttpHeaders(
      {'Authorization': 'bearer ' + token}
    )})
    .pipe(
      /*mergeMap(personList$ => this.interactionService.getAllInteractions()
        .pipe(
          map(interactionList$ => {
            for (let person$ of (personList$.data as Person[])) {
              person$.interactions = (interactionList$.data as Interaction[]).filter(x => Number(x.person?.toString()) === person$.idperson)
            }
            return personList$;
          })
        )
      ),*/
      catchError(err => { return throwError(() => err) })
    );
  }

  getPersonListHome(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.personServiceUrl + '-home-list')
    .pipe(
      catchError(this.handleError<ApiResponse>('getPerson'))
    );
  }

  savePerson(person: Person): Observable<ApiResponse> {
    const httpHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }
    return this.http.post<ApiResponse>(this.personServiceUrl, person, httpHeaders)
    .pipe(
      catchError(this.handleError<ApiResponse>('postPerson'))
    );
  }

  saveUserAndPerson(person: Person): Observable<ApiResponse> {
    const httpHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }
    return this.userService.saveUser(person.user).pipe(
      mergeMap(user$ => this.savePerson(person)
        .pipe(
          map(person$ => {
            (person$.data as Person).user = user$.data as User;
            return person$;
        }))
      )
    );
  }

  updatePerson(urlId: string, modifiedValues: {[key: string]: string | number | City | Locality | ServicePlace | CategoryService[]}): Observable<ApiEditResponse> {
    let token;
    if (isPlatformBrowser(this.platformId))
      token = sessionStorage.getItem('auth');
    const httpHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'bearer ' + token
      })
    }
    return this.http.put<ApiEditResponse>(this.personServiceUrl, {'urlId': urlId, 'data': modifiedValues}, httpHeaders)
    .pipe(
      catchError(err => { return throwError(() => err) })
    );
  }

  updateVisible(urlId: string, visible: number, field: string): Observable<ApiEditResponse> {
    let token;
    if (isPlatformBrowser(this.platformId))
      token = sessionStorage.getItem('auth');
    const httpHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'bearer ' + token
      })
    }
    return this.http.put<ApiEditResponse>(this.personServiceUrl + '-visible', {'urlId': urlId, 'visible': visible, 'field': field}, httpHeaders)
    .pipe(
      catchError(err => { return throwError(() => err) })
    );
  }

  deleteProfile(urlId: string, userName: string): Observable<ApiEditResponse> {
    let token;
    if (isPlatformBrowser(this.platformId))
      token = sessionStorage.getItem('auth');
    const httpHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'bearer ' + token
      }),
      body: {
        'userName': userName, 
        'urlId': urlId 
      }
    }
    return this.http.delete<ApiEditResponse>(this.personServiceUrl, httpHeaders)
    .pipe(
      catchError(err => { return throwError(() => err) })
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      return of(result as T);
    };
  }
}
